// LEGACY: Uses SCSS with CSS modules. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Uses @ion/components. New code should use @metarouter-private/mr-mui components instead.
// LEGACY: Contains significant business logic in component. Consider extracting logic into custom hooks, utility functions, or /shared/ directory.
import React, { useEffect, useState } from 'react';
import pt from 'prop-types';
import { useHistory, Route, Switch, useRouteMatch } from 'react-router-dom';
import { routes } from 'app/constants';
import { isEmpty as _isEmpty } from 'lodash';

import { useBuildStandaloneSync, useQueryClientSideIntegrations, useQueryIdentitySyncs } from '@ion/api';

import {
  MrAppMain,
  generateId,
  MrButton,
  MrCard,
  MrForm,
  MrH,
  useForm,
  formValidators,
  MrModal,
  MrSpinner,
  useToggle,
} from '@ion/components';
import { Stack, ConfirmDelete } from '@metarouter-private/mr-mui';
import {
  useBuildAjsFile,
  useQueryAnalyticsFile,
  useInsertAnalyticsFile,
  useInsertAnalyticsFileVersion,
} from '@ion/api';

import AnalyticsBuilderResults from './analytics-builder-results';
import AnalyticsBuilderSettings from './analytics-builder-settings';
import AnalyticsBuilderSyncs from './analytics-builder-syncs';
import AnalyticsBuilderIntegrations from './analytics-builder-integrations';
import AnalyticsBuilderCrossDomain from './AnalyticsBuilderCrossDomain';
import AnalyticsBuilderCrossDomainDirect from './AnalyticsBuilderCrossDomainDirect';

import s from './index.module.scss';
import createAjsBuilderFile from './create-ajs-builder-file';
import populateAjsBuilderForm from './populate-ajs-builder-form';
import getCrossDomainSettings from './getCrossDomainSettings';
import useFeatureEnabled from 'app/launch-darkly/use-feature-enabled';
import FieldMissingWarning from './field-missing-warning';
import { useConsentCategoriesContext } from 'app/context/consent-categories-context';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import { CUSTOM_IDENTITY_SYNC } from './identity-sync-card/components/consts';
import { useStagedIdentitySyncs } from './useStagedIdentitySyncs';
import { useLocation } from 'react-router-dom';
import { HOSTING_TYPES } from './analytics-builder-file-hosting';
import { handleDelete } from './handleDelete';
import RemoveFromBundleMessage from './RemoveFromBundleMessage';
import transformGoogleTagSettingsForDb from './transformGoogleTagSettingsForDb';
import useTrackAnalyticsFileSaved from './useTrackAnalyticsFileSaved';

import useLogFeatureDiscovery from 'app/analytics/useLogFeatureDiscovery';
import ANALYTICS_BUILDER_EVENTS from './analyticsBuilderEvents';
import BackButton from './BackButton';
import buildFileOptions from './buildFileOptions';
import AnalyticsBuilderKebabMenu from './AnalyticsBuilderKebabMenu/AnalyticsBuilderKebabMenu';
import useAnalyticsFileDownload from './useAnalyticsFileDownload';
import AjsFileUploadModal from './AjsFileUploadModal';
import buildVersionRows from './buildVersionRows';
import ResyncIntentProvider, {
  useResyncIntent,
} from 'app/components/analytics-builder/identity-sync-card/ResyncIntentContext';
import decorateSyncsWithVersionSettings from './decorateSyncsWithVersionSettings';

const AnalyticsBuilderInner = ({ name, writekey, clusterName }) => {
  const history = useHistory();
  const { getAllEnableResyncs } = useResyncIntent();
  const {
    location: {
      state: { pipelineId, clusterId, referrer },
    },
  } = history;
  const {
    ecmStandAloneSyncEnabled: isStandAloneSync,
    ecmStandAloneUnminifyFileEnabled: isUnminifyFileEnabled,
    botDetectionEnabled: isBotDetectionEnabled,
    ajsOptimizeFileBuildEnabled,
    builderServerQueueTasksEnabled,
    ecmConfigurableConsentEnabled,
    analyticsBuilderDownloadUploadEnabled,
    ecmAjsCrossDomainIdentitySyncEnabled: crossDomainV1Enabled,
    syncConfigRefreshMvpEnabled,
  } = useFeatureEnabled(
    FEATURE_FLAGS.ecmStandAloneSync,
    FEATURE_FLAGS.ecmStandAloneUnminifyFile,
    FEATURE_FLAGS.botDetection,
    FEATURE_FLAGS.ajsOptimizeFileBuild,
    FEATURE_FLAGS.builderServerQueueTasks,
    FEATURE_FLAGS.ecmConfigurableConsent,
    FEATURE_FLAGS.analyticsBuilderDownloadUpload,
    FEATURE_FLAGS.crossDomainV1,
    FEATURE_FLAGS.syncConfigRefreshMvp
  );
  const fileName = isStandAloneSync ? 'Sync Injector' : 'Analytics.js';

  const {
    data: analyticsFileData,
    loading: loadingAnalyticsFile,
    refetch: refetchAnalyticsFile,
  } = useQueryAnalyticsFile({
    variables: { pipelineId: pipelineId },
  });
  const { pathname } = useLocation();
  const isResultsPage = pathname.endsWith('results');

  const [insertAnalyticsFile, { isAllowed: updatePipelineAJSBuilderPermission }] = useInsertAnalyticsFile();
  const [insertAnalyticsFileVersion] = useInsertAnalyticsFileVersion();

  const { data: integrations, loading: loadingIntegrations } = useQueryClientSideIntegrations();
  const { data: identitySyncs, loading: loadingSyncs } = useQueryIdentitySyncs();
  const { consentCategories } = useConsentCategoriesContext();
  const formId = generateId();
  const { formState, validateField, resetError, submitForm, setField, removeFields } = useForm(formId);
  const { validateRequired, validateUrl, validateNumber, validateIngestionUrl } = formValidators;

  const { trackAnalyticsFileSaved } = useTrackAnalyticsFileSaved();
  useLogFeatureDiscovery(ANALYTICS_BUILDER_EVENTS.ANALYTICS_FILE_UPLOAD_FEATURE_VIEWED);

  // if the file is hosted on the MR Cluster, we want to show a different success message on upload
  const [buildAjsFile] = useBuildAjsFile({
    successMsg:
      formState?.fileHostingType?.value === HOSTING_TYPES.self
        ? `${fileName} for ${name} has been successfully built.`
        : undefined,
  });

  const [buildStandaloneSync] = useBuildStandaloneSync({
    successMsg:
      formState?.fileHostingType?.value === HOSTING_TYPES.self
        ? `${fileName} for ${name} has been successfully built.`
        : undefined,
  });

  const buildFile = isStandAloneSync ? buildStandaloneSync : buildAjsFile;

  const [stagedIntegrations, setStagedIntegrations] = useState([]);
  const [stagedIdentitySyncs, addIdentitySyncs] = useStagedIdentitySyncs();
  const [resultsData, setResultsData] = useState({});
  const [fileDownloading, setFileDownloading] = useState(false);
  const [generatedFile, setGeneratedFile] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [builderErrorModalOpen, setBuilderErrorModalOpen] = useState(false);
  const [builderErrorMessage, setBuilderErrorMessage] = useState('');
  const [analyticsSnippet, setAnalyticsSnippet] = useState('');
  const [formDataFilled, setFormDataFilled] = useState(false);
  const [isValidForm, setIsValidForm] = useState(null);
  const [currentClusterName] = useState(clusterName);

  useEffect(() => {
    if (!loadingIntegrations && !loadingSyncs && !loadingAnalyticsFile && !formDataFilled) {
      // set staged integrations and sync settings from saved analytics file
      if (analyticsFileData?.integrations) {
        setStagedIntegrations(analyticsFileData?.integrations.map(i => i.name));
      }
      if (!_isEmpty(analyticsFileData?.identitySyncs)) {
        addIdentitySyncs(analyticsFileData?.identitySyncs.map(i => i.name).filter(i => i !== 'crossDomain'));
      }

      // populate builder form with saved analytics file data
      populateAjsBuilderForm({ analyticsFileData, setField });

      setFormDataFilled(true);
    }
  }, [
    loadingIntegrations,
    loadingSyncs,
    loadingAnalyticsFile,
    formDataFilled,
    analyticsFileData,
    addIdentitySyncs,
    setField,
  ]);

  let stagedIdentitySyncsData = [];
  let stagedIntegrationsData = [];

  // generate UI cards for staged identity syncs and integrations
  stagedIdentitySyncsData = stagedIdentitySyncs
    ?.map(sync => {
      // Creates the card for CUSTOM_IDENTITY_SYNC, this does not come from the API
      if (sync === CUSTOM_IDENTITY_SYNC.REQUEST_NAME) {
        return { friendlyName: CUSTOM_IDENTITY_SYNC.DESCRIPTION, name: CUSTOM_IDENTITY_SYNC.REQUEST_NAME, fields: [] };
      }
      return identitySyncs.find(s => s.name === sync);
    })
    .filter(s => s !== undefined);

  stagedIntegrationsData = stagedIntegrations
    ?.map(integration => {
      return integrations.find(i => i.name === integration);
    })
    .filter(i => i !== undefined);

  const { handleDownload } = useAnalyticsFileDownload({
    formState,
    analyticsFileData,
    stagedIdentitySyncsData,
    stagedIntegrationsData,
    writekey,
    consentCategories,
    pipelineName: name,
  });

  const [uploadModalOpen, toggleUploadModal] = useToggle(false);

  const onFormSubmit = () => {
    const { data, isValid } = submitForm();

    if (isValid) {
      setIsValidForm(true);
      try {
        const crossDomainSettings = getCrossDomainSettings(analyticsFileData);

        const { analyticsSnippet, settingsTemplate } = createAjsBuilderFile({
          stagedIdentitySyncsData,
          stagedIntegrationsData,
          data,
          writekey,
          crossDomainSettings,
          optimizeBuild: ajsOptimizeFileBuildEnabled,
          consentCategories,
          queueTasks: builderServerQueueTasksEnabled,
          hasConsentModule: ecmConfigurableConsentEnabled,
          isStandAloneSync,
        });
        setAnalyticsSnippet(analyticsSnippet);
        setResultsData(settingsTemplate);
        setFileDownloading(true);
        const identitySyncsForInsert =
          syncConfigRefreshMvpEnabled && !isStandAloneSync
            ? attachEnableResync(settingsTemplate.syncInjectorSettings.syncs, getAllEnableResyncs())
            : settingsTemplate.syncInjectorSettings.syncs;
        const config = {
          settings: settingsTemplate,
          identitySyncs: identitySyncsForInsert,
          integrations: settingsTemplate.integrations,
          pipelineId,
          googleTagSettings: transformGoogleTagSettingsForDb(settingsTemplate.syncInjectorSettings.googleGtagSettings),
          previousIdentitySyncs: analyticsFileData?.identitySyncs,
        };
        // store/update generated file settings in database for future access
        insertAnalyticsFile(config)
          .then(res => {
            const analyticsFileId = res?.data?.insert_analytics_files_one?.id;
            const identitySyncs = res.data.insert_analytics_files_one.analytics_file_identity_syncs;

            const buildOptions = buildFileOptions(settingsTemplate, isStandAloneSync);
            if (syncConfigRefreshMvpEnabled && !isStandAloneSync) {
              buildOptions.settingsJson.syncInjectorSettings.syncs = decorateSyncsWithVersionSettings({
                syncs: buildOptions.settingsJson.syncInjectorSettings.syncs,
                postInsertIdentitySyncs: identitySyncs,
              });
            }
            buildFile(buildOptions)
              .then(async ({ data }) => {
                const key = isStandAloneSync ? 'build_standalone_sync_file' : 'build_ajs_file';

                const buildFileResult = data[key];

                const status = buildFileResult?.status;
                if (status !== 200) {
                  const errorMessage =
                    buildFileResult?.data === undefined
                      ? 'Server error, file could not be build'
                      : buildFileResult.data;
                  setBuilderErrorModalOpen(true);
                  setBuilderErrorMessage(errorMessage);
                  setFileDownloading(false);
                } else {
                  setFileDownloading(false);
                  setGeneratedFile(buildFileResult?.data);

                  const versionInfo = buildFileResult?.versionInfo;
                  if (versionInfo && analyticsFileId) {
                    const syncVersionRows = buildVersionRows({ versionInfo, identitySyncs });
                    insertAnalyticsFileVersion({
                      analyticsFileId,
                      buildType: versionInfo.buildType,
                      syncInjectorVersion: versionInfo.syncInjectorVersion,
                      syncVersionRows,
                    }).catch(error => {
                      console.error('Failed to insert version info:', error);
                    });
                  }

                  history.push(`${routes.analyticsJSBuilder}/results`, {
                    pipelineId,
                    name,
                    writekey,
                    clusterId,
                    clusterName: currentClusterName,
                    referrer,
                    analyticsFileId,
                  });
                  trackAnalyticsFileSaved(
                    settingsTemplate.integrations,
                    settingsTemplate.syncInjectorSettings.syncs,
                    analyticsFileData,
                    pipelineId,
                    name,
                    analyticsFileId
                  );
                  // refetch the analytics file to get the latest data and allow for the form to be refetched
                  await refetchAnalyticsFile();
                  setFormDataFilled(false);
                }
              })
              .catch(() => {
                setFileDownloading(false);
              });
          })
          .catch(() => {
            setFileDownloading(false);
          });
      } catch (e) {
        const errorMessage = e?.message === undefined ? 'Failed to generate settings for file' : e.message;
        setFileDownloading(false);
        setBuilderErrorModalOpen(true);
        setBuilderErrorMessage(errorMessage);
      }
    } else {
      setIsValidForm(false);
    }
  };

  let { path } = useRouteMatch();

  return (
    <>
      <MrAppMain
        styleNames="form"
        loading={loadingIntegrations || loadingSyncs}
        header={<BackButton pipelineId={pipelineId} name={name} isResultsPage={isResultsPage} />}
      >
        <MrCard className={s.formCard}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <MrH h="h3" sans styleNames="noMargin">
              {isResultsPage ? `${fileName} Summary` : `Edit ${fileName}`}
            </MrH>
            {analyticsBuilderDownloadUploadEnabled && !isResultsPage && (
              <AnalyticsBuilderKebabMenu onDownload={handleDownload} onUpload={toggleUploadModal} />
            )}
          </Stack>
          <Switch>
            <Route exact path={path}>
              <MrForm onSubmit={onFormSubmit} id={formId} fullWidth>
                <AnalyticsBuilderSettings
                  writekey={writekey}
                  formState={formState}
                  setField={setField}
                  validateField={validateField}
                  validateRequired={validateRequired}
                  validateUrl={validateUrl}
                  validateIngestionUrl={validateIngestionUrl}
                  resetError={resetError}
                  isStandAloneSync={isStandAloneSync}
                  isUnminifyFileEnabled={isUnminifyFileEnabled}
                  isBotDetectionEnabled={isBotDetectionEnabled}
                  fileNameText={fileName}
                />

                <AnalyticsBuilderSyncs
                  formState={formState}
                  identitySyncs={identitySyncs}
                  stagedIdentitySyncsData={stagedIdentitySyncsData}
                  setField={setField}
                  validateField={validateField}
                  validateRequired={validateRequired}
                  validateNumber={validateNumber}
                  resetError={resetError}
                  setModalOpen={setModalOpen}
                />

                <AnalyticsBuilderCrossDomain
                  formState={formState}
                  setField={setField}
                  validateField={validateField}
                  resetError={resetError}
                />

                <AnalyticsBuilderCrossDomainDirect
                  formState={formState}
                  setField={setField}
                  validateField={validateField}
                  resetError={resetError}
                  crossDomainV1Enabled={crossDomainV1Enabled}
                />

                {!isStandAloneSync && (
                  <AnalyticsBuilderIntegrations
                    formState={formState}
                    integrations={integrations}
                    stagedIntegrations={stagedIntegrations}
                    stagedIntegrationsData={stagedIntegrationsData}
                    setField={setField}
                    validateField={validateField}
                    validateRequired={validateRequired}
                    validateNumber={validateNumber}
                    resetError={resetError}
                    setModalOpen={setModalOpen}
                    setStagedIntegrations={setStagedIntegrations}
                  />
                )}

                <footer className={s.footer}>
                  <div className={s.buttonContainer}>
                    {fileDownloading && (
                      <MrSpinner
                        className={s.spinner}
                        textPosition="right"
                        size="md"
                        message={`Building ${fileName} file...`}
                      />
                    )}
                    {isValidForm === false && (
                      <FieldMissingWarning
                        formState={formState}
                        integrations={integrations}
                        identitySyncs={identitySyncs}
                      />
                    )}
                    <MrButton
                      className={s.button}
                      testId="buildFile"
                      text="Save and Build File"
                      disabled={!updatePipelineAJSBuilderPermission || fileDownloading}
                    />
                  </div>
                </footer>
              </MrForm>
            </Route>

            <Route path={`${path}/results`}>
              <AnalyticsBuilderResults
                writekey={writekey}
                resultsData={resultsData}
                analyticsSnippet={analyticsSnippet}
                generatedFile={generatedFile}
                pipelineName={name}
                clusterName={currentClusterName}
                isStandAloneSync={isStandAloneSync}
                isUnminifyFileEnabled={isUnminifyFileEnabled}
              />
            </Route>
          </Switch>
        </MrCard>
      </MrAppMain>

      {builderErrorModalOpen && (
        <MrModal
          closeModal={() => {
            setBuilderErrorModalOpen(false);
            setBuilderErrorMessage('');
          }}
          styleNames="basic"
        >
          <div className={s.header}>
            <MrH h="h3" sans styleNames="noMargin">
              Error
            </MrH>
          </div>
          <div className={s.content}>{builderErrorMessage}</div>
        </MrModal>
      )}

      {modalOpen && (
        <ConfirmDelete
          open={true}
          onClose={() => setModalOpen(false)}
          title={`Remove ${modalOpen.data.friendlyName} from this bundle?`}
          message={<RemoveFromBundleMessage type={modalOpen.type} fileName={fileName} />}
          onConfirm={() => {
            handleDelete(
              modalOpen,
              stagedIdentitySyncs,
              addIdentitySyncs,
              stagedIntegrations,
              setStagedIntegrations,
              removeFields
            );
            setModalOpen(false);
          }}
          ConfirmProps={{ 'data-test-id': 'ConfirmRemoveFromBundle' }}
        />
      )}

      <AjsFileUploadModal
        open={uploadModalOpen}
        onClose={toggleUploadModal}
        setField={setField}
        addIdentitySyncs={addIdentitySyncs}
        setStagedIntegrations={setStagedIntegrations}
        formState={formState}
        stagedIdentitySyncs={stagedIdentitySyncs}
        stagedIntegrations={stagedIntegrations}
      />
    </>
  );
};

AnalyticsBuilderInner.propTypes = {
  writekey: pt.string,
  name: pt.string,
  clusterName: pt.string,
};

const AnalyticsBuilder = props => {
  const history = useHistory();
  const pipelineId = history.location.state?.pipelineId;
  const { data: analyticsFileData } = useQueryAnalyticsFile({ variables: { pipelineId } });
  return (
    <ResyncIntentProvider initialIdentitySyncs={analyticsFileData?.identitySyncs}>
      <AnalyticsBuilderInner {...props} />
    </ResyncIntentProvider>
  );
};

AnalyticsBuilder.propTypes = {
  writekey: pt.string,
  name: pt.string,
  clusterName: pt.string,
};

export default AnalyticsBuilder;

function attachEnableResync(syncs, enableResyncMap) {
  return syncs.map(sync => ({
    ...sync,
    enableResyncOnSettingsChange: enableResyncMap[sync.name] === true,
  }));
}
